import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Search, RefreshCw, Edit, Users, Building2, User, ShoppingCart, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Iter 286.l — Directorio Unificado
 *
 * Fusiona alegra_contacts + proveedores + clientes en una sola vista, indexado
 * por RUT canónico. Filtros: Todos | Proveedores | Clientes | Ambos.
 * La acción "Editar" propaga el cambio a TODAS las colecciones (via /api/directorio/{rut}).
 */
export default function DirectorioPage() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ total_directorio: 0, solo_proveedores: 0, solo_clientes: 0, ambos: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('todos');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 100;
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const params = { q, tipo, limit, skip };
      if (forceRefresh) params.refresh = true;
      const res = await api.get('/directorio', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setKpis(res.data.kpis || {});
    } catch (e) {
      toast.error('Error cargando directorio', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [q, tipo, skip]);

  useEffect(() => {
    const t = setTimeout(() => load(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setSkip(0);
    await load(true);
    setRefreshing(false);
    toast.success('Directorio actualizado');
  };

  const openEdit = (item) => {
    setEditing(item);
    setEditForm({
      nombre: item.nombre || '',
      email: item.email || '',
      email_cecp: item.email_cecp || '',
      telefono: item.telefono || '',
      ciudad: item.ciudad || '',
      direccion: item.direccion || '',
    });
  };

  const guardarEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await api.put(`/directorio/${editing.rut}`, editForm);
      const d = res.data;
      const impacto = [];
      if (d.alegra_contacts_updated > 0) impacto.push(`${d.alegra_contacts_updated} en alegra_contacts`);
      if (d.proveedores_updated > 0) impacto.push(`${d.proveedores_updated} en proveedores`);
      if (d.clientes_updated > 0) impacto.push(`${d.clientes_updated} en clientes`);
      toast.success(`Actualizado en ${impacto.join(' · ') || '0 colecciones'}`);
      setEditing(null);
      await load(true);
    } catch (e) {
      toast.error('Error al guardar', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const tipoBadge = (item) => {
    if (item.es_proveedor && item.es_cliente) {
      return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" data-testid={`tipo-${item.rut}`}>PC</span>;
    }
    if (item.es_proveedor) {
      return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700" data-testid={`tipo-${item.rut}`}>P</span>;
    }
    if (item.es_cliente) {
      return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`tipo-${item.rut}`}>C</span>;
    }
    return <span className="text-[10px] text-muted-foreground">–</span>;
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" /> Directorio Unificado
          </h1>
          <p className="text-sm text-muted-foreground">
            Un solo listado de contactos: Proveedores, Clientes y ambos. Editar aquí actualiza todas las colecciones.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="btn-refresh-directorio">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refrescar caché
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Total en Directorio</div>
            <div className="text-xl font-bold flex items-center gap-2"><Users className="h-4 w-4" /> {kpis.total_directorio}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Sólo Proveedores</div>
            <div className="text-xl font-bold flex items-center gap-2 text-orange-600"><ShoppingCart className="h-4 w-4" /> {kpis.solo_proveedores}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Sólo Clientes</div>
            <div className="text-xl font-bold flex items-center gap-2 text-emerald-600"><Building2 className="h-4 w-4" /> {kpis.solo_clientes}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Ambos (P + C)</div>
            <div className="text-xl font-bold flex items-center gap-2 text-purple-600"><ArrowLeftRight className="h-4 w-4" /> {kpis.ambos}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, RUT o email..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            className="pl-9"
            data-testid="input-search-directorio"
          />
        </div>
        <div className="flex gap-1">
          {[
            { k: 'todos', label: 'Todos' },
            { k: 'proveedor', label: 'Proveedores' },
            { k: 'cliente', label: 'Clientes' },
            { k: 'ambos', label: 'Ambos (P+C)' },
          ].map((t) => (
            <Button
              key={t.k}
              variant={tipo === t.k ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setTipo(t.k); setSkip(0); }}
              data-testid={`filter-tipo-${t.k}`}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[70px]">Tipo</TableHead>
                <TableHead>Nombre / Razón Social</TableHead>
                <TableHead className="w-[130px]">RUT</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-[130px]">Teléfono</TableHead>
                <TableHead className="w-[130px]">Ciudad</TableHead>
                <TableHead className="w-[80px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin resultados</TableCell></TableRow>
              ) : (
                items.map((it) => (
                  <TableRow key={it.rut} data-testid={`row-${it.rut}`}>
                    <TableCell>{tipoBadge(it)}</TableCell>
                    <TableCell className="font-medium">
                      {it.nombre}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        sources: {it.sources?.join(' · ') || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{it.rut}</TableCell>
                    <TableCell className={it.email ? '' : 'text-muted-foreground italic text-xs'}>
                      <div>{it.email || '(sin email)'}</div>
                      {it.email_cecp && it.email_cecp !== it.email && (
                        <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5" title="Email para consultas de cobranza (CxP)">
                          🔵 CxP: {it.email_cecp}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{it.telefono || '—'}</TableCell>
                    <TableCell className="text-xs">{it.ciudad || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`btn-edit-${it.rut}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginación básica */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Mostrando {skip + 1}–{Math.min(skip + limit, total)} de {total}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="btn-prev-page">Anterior</Button>
            <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="btn-next-page">Siguiente</Button>
          </div>
        </div>
      )}

      {/* Modal edición */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-edit-directorio">
          <DialogHeader>
            <DialogTitle>
              Editar contacto — {editing?.rut}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Estos cambios se sincronizan automáticamente con: {editing?.sources?.join(' · ') || 'las colecciones fuente'}.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre / Razón Social</Label>
              <Input value={editForm.nombre || ''} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} data-testid="input-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email compras / cotizaciones</Label>
                <Input value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="compras@proveedor.cl" data-testid="input-email" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Se usa para enviar solicitudes de cotización y OC.</p>
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={editForm.telefono || ''} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} data-testid="input-telefono" />
              </div>
            </div>
            <div>
              <Label>Email CxP / cobranzas <span className="text-[10px] text-muted-foreground font-normal">(sólo proveedores)</span></Label>
              <Input
                value={editForm.email_cecp || ''}
                onChange={(e) => setEditForm({ ...editForm, email_cecp: e.target.value })}
                placeholder="cobranzas@proveedor.cl"
                disabled={!editing?.es_proveedor}
                data-testid="input-email-cecp"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Se usa para consultar estados de cuenta / cobranzas. Si es el mismo que el de compras, déjalo vacío.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ciudad</Label>
                <Input value={editForm.ciudad || ''} onChange={(e) => setEditForm({ ...editForm, ciudad: e.target.value })} data-testid="input-ciudad" />
              </div>
              <div>
                <Label>Dirección</Label>
                <Input value={editForm.direccion || ''} onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })} data-testid="input-direccion" />
              </div>
            </div>
            {editing && (
              <div className="text-xs bg-slate-50 dark:bg-slate-900/40 rounded p-2 space-y-0.5">
                <div className="flex items-center gap-1"><User className="h-3 w-3" /> Es proveedor: {editing.es_proveedor ? 'Sí' : 'No'}</div>
                <div className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Es cliente: {editing.es_cliente ? 'Sí' : 'No'}</div>
                {editing.alegra_id && <div>alegra_id: {editing.alegra_id}</div>}
                {editing.proveedor_id && <div>proveedor_id: {editing.proveedor_id}</div>}
                {editing.cliente_id && <div>cliente_id: {editing.cliente_id}</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={guardarEdit} disabled={saving} data-testid="btn-save-directorio">
              {saving ? 'Guardando...' : 'Guardar en todas las colecciones'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
